import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SharePoemButton from './SharePoemButton';

/**
 * Reusable poem card used by the Poetry Room + Home page + single-poem page.
 *
 * Props:
 *  - poem: the poem object (from data/poems.js)
 *  - size: 'sm' | 'md' | 'lg' — controls typography scale
 *  - showShare: bool — render the "Share this poem" button
 *  - showPermalink: bool — render an "Open poem" link (deep link to /poetry/:id)
 */
const PoemDisplay = ({ poem, size = 'md', showShare = false, showPermalink = false }) => {
  const titleSize =
    size === 'lg' ? 'text-5xl md:text-7xl' :
    size === 'sm' ? 'text-2xl md:text-3xl' :
                    'text-3xl md:text-4xl';
  const verseSize =
    size === 'lg' ? 'text-xl md:text-2xl' :
    size === 'sm' ? 'text-sm md:text-base' :
                    'text-base md:text-lg';

  return (
    <article
      className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-3xl p-8 md:p-12"
      data-testid={`poem-${poem.id}`}
    >
      <h3
        className={`${titleSize} font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-2 leading-tight`}
        style={{ fontFamily: '"Bebas Neue", sans-serif' }}
      >
        {poem.title}
      </h3>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-gray-500 text-sm mb-6">
        <span>by {poem.author}</span>
        {poem.emotions?.length > 0 && (
          <span className="flex flex-wrap gap-1">
            {poem.emotions.map((e) => (
              <span
                key={e}
                className="inline-block text-[10px] uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5"
              >
                {e.replace('-', ' ')}
              </span>
            ))}
          </span>
        )}
      </div>

      <div
        className={`${verseSize} text-gray-100 leading-relaxed font-serif whitespace-pre-line mb-6`}
      >
        {poem.verses.join('\n')}
      </div>

      {poem.backstory && (
        <div className="border-l-2 border-[#D4AF37]/30 pl-4 py-2 mb-6">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">The story behind it</p>
          <p className="text-gray-400 italic text-sm md:text-base leading-relaxed">
            {poem.backstory}
          </p>
        </div>
      )}

      {(showShare || showPermalink) && (
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#D4AF37]/10">
          {showShare && <SharePoemButton poem={poem} />}
          {showPermalink && (
            <Link
              to={`/poetry/${poem.id}`}
              data-testid={`poem-open-${poem.id}`}
              className="inline-flex items-center gap-1 text-sm font-bold text-[#D4AF37] hover:underline"
            >
              Open poem
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      )}
    </article>
  );
};

export default PoemDisplay;
